import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Skeleton, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, PieChart, Pie, Cell
} from 'recharts';
import { especializadaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

const COLORS = ['#2563eb', '#16a34a', '#eab308', '#ef4444', '#8b5cf6', '#06b6d4'];

const EspecializadaProspeccao: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    especializadaService.getProspeccao(municipioAtivo.id, dataInicio || undefined, dataFim || undefined)
      .then(setData)
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  const gapData = useMemo(() => {
    if (!data?.parametrosMS) return [];
    return data.parametrosMS.map((p: any) => {
      const demandaEsperada = Number(p.demanda_esperada_mensal);
      const capacidade = Number(p.capacidade_instalada_mensal);
      const gap = demandaEsperada - capacidade;
      return {
        especialidade: p.especialidade?.substring(0, 20),
        especialidadeFull: p.especialidade,
        demanda_esperada: demandaEsperada,
        capacidade_instalada: capacidade,
        gap: Math.abs(gap),
        status: gap > 0 ? 'deficit' : 'suficiente',
        cobertura: capacidade > 0 ? Math.round((capacidade / demandaEsperada) * 100) : 0,
      };
    }).sort((a: any, b: any) => b.demanda_esperada - a.demanda_esperada);
  }, [data]);

  const regulacaoData = useMemo(() => {
    if (!data?.regulacaoPorSituacao) return [];
    const labels: Record<string, string> = { REC: 'Recebida', FIE: 'Fila Espera', FAL: 'Falta', CAN: 'Cancelada', EFE: 'Efetivada', AGE: 'Agendada' };
    return data.regulacaoPorSituacao.map((r: any) => ({ name: labels[r.situacao] || r.situacao, value: Number(r.total) }));
  }, [data]);

  const dadosContexto = useMemo(() => {
    if (!data) return '';
    const topGaps = gapData.filter((g: any) => g.status === 'deficit').slice(0, 5);
    return `Prospeccao Assistencial - ${municipioAtivo?.nome || 'Contagem/MG'}
Total fila de espera: ${Number(data.kpis?.total_fila_espera || 0).toLocaleString('pt-BR')}
Especialidades com fila: ${data.kpis?.especialidades_com_fila || 0}
Media dias espera: ${data.kpis?.media_geral_dias_espera || 0}
Maiores gaps (deficit mensal): ${topGaps.map((g: any) => g.especialidadeFull + ': -' + g.gap).join(', ')}`;
  }, [data, gapData, municipioAtivo]);

  if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={500} /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Prospecção Assistencial</Typography>
          <Typography variant="body2" color="text.secondary">Capacidade instalada vs demanda por especialidade</Typography>
        </Box>
        <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={(di, df) => { setDataInicio(di); setDataFim(df); }} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><KpiCard titulo="Fila de Espera" valor={Number(data?.kpis?.total_fila_espera || 0).toLocaleString('pt-BR')} cor="#ef4444" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Especialidades c/ Fila" valor={data?.kpis?.especialidades_com_fila || 0} cor="#eab308" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Capacidade Pactuada" valor={Number(data?.kpis?.total_capacidade_pactuada || 0).toLocaleString('pt-BR')} cor="#16a34a" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Média Dias Espera" valor={`${data?.kpis?.media_geral_dias_espera || 0} dias`} cor="#2563eb" /></Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <AnalisePredicaoCard manualTrigger={true}
          municipioId={municipioAtivo?.id || 0}
          contexto="dashboard"
          dadosContexto={dadosContexto}
          prompt="Voce e um analista de regulacao em saude. Analise os gaps entre capacidade instalada e demanda. Identifique: 1) Especialidades criticas com maior deficit, 2) Impacto na fila de espera, 3) Recomendacoes de adequacao da oferta, 4) Prioridades de investimento. Seja conciso."
        />
      </Box>

      <Grid container spacing={2}>
        {/* Gráfico Capacidade vs Demanda */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Capacidade Instalada vs Demanda Esperada (mensal)</Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={gapData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} tickFormatter={(v: any) => v >= 1000 ? `${(v/1000).toFixed(1)}K` : String(v)} />
                <YAxis type="category" dataKey="especialidade" width={150} fontSize={10} />
                <Tooltip formatter={(v: any) => v.toLocaleString('pt-BR')} />
                <Legend />
                <Bar dataKey="capacidade_instalada" name="Capacidade" fill="#16a34a" radius={[0, 4, 4, 0]} />
                <Bar dataKey="demanda_esperada" name="Demanda Esperada" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Regulação */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Regulação por Situação</Typography>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie data={regulacaoData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2}>
                  {regulacaoData.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v: any) => v.toLocaleString('pt-BR')} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Tabela de Gap */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Análise de Gap por Especialidade</Typography>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Especialidade</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Demanda Esperada/mês</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Capacidade/mês</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Gap</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Cobertura</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gapData.map((g: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{g.especialidadeFull}</TableCell>
                      <TableCell align="right">{g.demanda_esperada.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">{g.capacidade_instalada.toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right" sx={{ color: g.status === 'deficit' ? '#ef4444' : '#16a34a', fontWeight: 600 }}>
                        {g.status === 'deficit' ? '-' : '+'}{g.gap.toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell align="right">{g.cobertura}%</TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={g.status === 'deficit' ? 'Déficit' : 'Suficiente'}
                          color={g.status === 'deficit' ? 'error' : 'success'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Demanda Reprimida (Fila) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Demanda Reprimida - Fila de Espera</Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={(data?.demandaReprimida || []).slice(0, 15).map((d: any) => ({ ...d, nome: (d.especialidade || '').substring(0, 20), total: Number(d.total_fila) }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" fontSize={11} tickFormatter={(v: any) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
                <YAxis type="category" dataKey="nome" width={160} fontSize={10} />
                <Tooltip formatter={(v: any) => [v.toLocaleString('pt-BR'), 'Na fila']} />
                <Bar dataKey="total" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <ChatIAButton contexto="dashboard" dadosContexto={dadosContexto} />
    </Box>
  );
};

export default EspecializadaProspeccao;
